import { getFormatedDate } from "../../utils/common";
import toast from "react-hot-toast";
import { printBillViaBluetooth, hasBluetoothBillPrinterConfigured } from "../../services/BillPrintService";
import {
  THERMAL_BILL_BROWSER_BASE_CSS,
  openThermalBillPrintWindow,
} from "../../utils/thermalBillBrowserPrint.js";

// Main print function
export const PrintBills = async (
  cartItems,
  hotelData,
  roomData,
  billNo,
  billTxnLogs,
  customer,
  GST,
  printers = [],
  hightLightOrderIDLastNDigits = 0,
  couponMetadata
) => {
  const date = new Date();
  const formattedDateTime = getFormatedDate(date);

  const billPrinters = printers.filter(
    (printer) =>
      printer.type !== "bluetooth" &&
      (printer.location === "bill" || printer.location === "all") &&
      printer.isConnected
  );

  if (billTxnLogs.showGSTFooter === undefined) {
    billTxnLogs.showGSTFooter = true;
  }

  const orderData = {
    validItems: cartItems,
    hotelData,
    roomData,
    billNo,
    billTxnLogs,
    customer,
    GST,
    formattedDateTime,
    hightLightOrderIDLastNDigits,
    couponMetadata,
  };

  const bluetoothPrinted = await printBillViaBluetooth(orderData, printers);
  if (bluetoothPrinted) {
    return;
  }

  if (hasBluetoothBillPrinterConfigured(printers)) {
    return;
  }

  if (billPrinters && billPrinters.length > 0) {
    billPrinters.forEach((printer) => {
      printer.print(orderData, "bill");
    });
    return;
  }

  // If no Web Bluetooth / server bill printer, use browser HTML print (window.print)
  printBrowserBill({
    cartItems,
    hotelData,
    roomData,
    billNo,
    billTxnLogs,
    customer,
    GST,
    formattedDateTime,
    hightLightOrderIDLastNDigits,
    couponMetadata,
  });
};

// Browser print fallback
function printBrowserBill({
  cartItems,
  hotelData,
  roomData,
  billNo,
  billTxnLogs,
  customer,
  GST,
  formattedDateTime,
  hightLightOrderIDLastNDigits,
  couponMetadata,
}) {
  const validItems = cartItems.filter((item) => !item.canceled);

  let customerDetailsTemplate = "";
  if (roomData.room === "Parcel") {
    if (customer.name !== "NA" && customer.name !== "") {
      customerDetailsTemplate = `
        <div class="divider"></div>
        <div class="font-md">
          <p style="margin: 0 0 4px 0;"><strong>Customer:</strong> ${
            customer.name
          }</p>
          ${
            customer.phone !== 0 && customer.phone !== "NA"
              ? `<p style="margin: 0 0 4px 0;"><strong>Ph:</strong> ${customer.phone}</p>`
              : ""
          }
          ${
            customer.address !== "NA" && customer.address !== ""
              ? `<p style="margin: 0 0 4px 0;"><strong>Add:</strong> ${customer.address}</p>`
              : ""
          }
        </div>
      `;
    }
  } else {
    if (customer.name !== "NA" && customer.name !== "") {
      customerDetailsTemplate = `
        <div class="divider"></div>
        <div class="font-md">
          <p style="margin: 0 0 4px 0;"><strong>Customer:</strong> ${customer.name}</p>
        </div>
      `;
    }
  }

  let normalBillNo = billNo.toString();
  if (hightLightOrderIDLastNDigits > 0) {
    normalBillNo = normalBillNo.slice(0, -hightLightOrderIDLastNDigits);
    normalBillNo += ` - <b>${normalBillNo.slice(
      -hightLightOrderIDLastNDigits
    )}</b>`;
  }

  // Build the HTML for the bill
  const billHtml = `
    <html>
      <head>
        <title>Bill Receipt - ${billNo}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body {
            margin: 0;
            font-size: 14px;
            padding: 0;
            font-family: Arial, sans-serif;
            background-color: white;
            line-height: 1.3;
          }
          
          .bill-container {
            width: calc(100% - 12px);
          }
          
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .text-left { text-align: left; }
          .text-bold { font-weight: bold; }
          
          .divider {
            margin: 10px 0;
            border-top: 1px dashed #000;
          }
          
          .mb-12 { margin-bottom: 12px; }
          .mb-8 { margin-bottom: 8px; }
          .mb-6 { margin-bottom: 6px; }
          .mt-12 { margin-top: 12px; }
          
          .font-sm { font-size: 12px; }
          .font-md { font-size: 13px; }
          .font-lg { font-size: 15px; }
          .font-xl { font-size: 16px; }
          
          table {
            width: 100%;
            border-collapse: collapse;
          }
          
          .item-row td {
            padding: 4px 0;
            vertical-align: top;
          }
          
          .capitalize {
            text-transform: capitalize;
          }
          
          h2 {
            font-size: 18px;
            font-weight: bold;
            margin: 0 0 6px 0;
            line-height: 1.2;
          }
          
          p {
            margin: 0 0 6px 0;
            line-height: 1.2;
          }
          
          .header-info {
            font-size: 12px;
            line-height: 1.2;
            margin: 0 0 4px 0;
          }
          
          .bill-info-table td {
            font-size: 13px;
            padding: 2px 0;
          }
          
          .items-header th {
            font-size: 13px;
            padding: 4px 0;
            border-bottom: 1px solid #000;
            font-weight: bold;
          }
          
          .item-name {
            font-size: 13px;
            font-weight: bold;
          }
          
          .variant-name {
            font-size: 13px;
            color: black;
            font-weight: normal;
          }
          
          .summary-table td {
            padding: 2px 0;
            font-size: 13px;
          }
          
          .total-row {
            border-top: 2px solid #000;
            font-weight: bold;
            font-size: 15px;
          }
          
          .footer-text {
            font-size: 11px;
            text-align: center;
          }
        ${THERMAL_BILL_BROWSER_BASE_CSS}
        </style>
      </head>
      <body>
        <div class="bill-container">
          <!-- Header -->
          <div class="text-center mb-12">
            <h2>${hotelData.name}</h2>
            <p class="header-info">${hotelData.address}</p>
            ${
              hotelData.contact
                ? `<p class="header-info">${hotelData.contact}</p>`
                : ""
            }
            ${
              billTxnLogs.gst
                ? `<p class="header-info">GSTN: ${GST.gstNumber}</p>`
                : ""
            }
            ${
              GST.fassi_no !== ""
                ? `<p class="header-info">FSSAI: ${GST.fassi_no}</p>`
                : ""
            }
            <div class="divider"></div>
          </div>

          <!-- Bill Info -->
          <div class="mb-12">
            <table class="bill-info-table">
              <tr>
                <td><span class="font-sm">Bill No: ${normalBillNo}</span></td>
                <td class="text-right"><strong>Date:</strong> ${formattedDateTime}</td>
              </tr>
              <tr>
                <td><strong>Table: <span class="font-xl">${roomData.table}</span></strong></td>
              </tr>
            </table>
            
            ${customerDetailsTemplate}
            
            <div class="divider"></div>
          </div>

          ${
            couponMetadata
              ? `
                <p class="text-left font-md">Coupon Applied:</p>
                <div style="
                  border: 1px dashed #000;
                  padding: 8px 15px;
                  margin: 10px auto;
                  text-align: center;
                  border-radius: 8px;
                ">
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <div style="font-size: 16px; font-weight: bold;">${couponMetadata.code}</div>
                    <div style="font-size: 16px; font-weight: bold;">${couponMetadata.value}/- off</div>
                  </div>
                </div>
              `
              : ""
          }

          <!-- Items -->
          <div class="mb-12">
            <table>
              <thead>
                <tr class="items-header">
                  <th class="text-left">Item</th>
                  <th class="text-left" style="width: 40px;">Qty</th>
                  <th class="text-right" style="width: 40px;">Price</th>
                  <th class="text-right" style="width: 50px;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${validItems
                  .map(
                    (item) => {
                      const addonsList = item.addOn || item.addOns || item.addons || [];
                      const addonSum = addonsList.reduce((a, b) => a + Number(b.price || 0), 0);
                      const displayPrice = Number(item.price);
                      return `
                  <tr class="item-row">
                    <td class="text-left">
                      <div class="item-name">${item.name}</div>
                      ${
                        item.isVariant
                          ? `<div class="variant-name">- ${item.variantName}</div>`
                          : ""
                      }
                      ${
                        addonsList.length > 0
                          ? `<div style="font-size: 11px; color: #555; margin-top: 2px;">
                              ${addonsList.map(a => `<div style="margin-left: 5px;">* ${a.name} (₹${a.price})</div>`).join('')}
                             </div>`
                          : ""
                      }
                    </td>
                    <td class="text-left font-md">${item.quantity}</td>
                    <td class="text-right font-md">
                      ${
                        item.discountAmount > 0
                          ? `<div>
                             
                              <span>${Number(
                                displayPrice - item.discountAmount
                              ).toFixed(2)}</span>
                                   <br/>
                               <span style="text-decoration: line-through; font-size: 11px; color: #666;">${Number(
                                 displayPrice
                               ).toFixed(2)}</span>
                         
                            </div>`
                          : Number(displayPrice).toFixed(2)
                      }
                    </td>
                    <td class="text-right font-md"><strong>${(
                      (Number(item.quantity) * (displayPrice - (Number(item.discountAmount) || 0) + addonSum))
                    ).toFixed(2)}</strong></td>
                  </tr>
                `;
                    }
                  )
                  .join("")}
              </tbody>
            </table>
            <div class="divider"></div>
          </div>

          <!-- Summary -->
          <div class="mb-12">
            <table class="summary-table">
              <tr>
                <td class="text-right"><strong>Sub Total:</strong></td>
                <td class="text-right" style="width: 80px;"><strong>${Number(
                  billTxnLogs.subTotal
                ).toFixed(2)}</strong></td>
              </tr>
              
              ${
                billTxnLogs.discountFlat > 0
                  ? `
                <tr>
                  <td class="text-right"><strong>Flat Discount:</strong></td>
                  <td class="text-right"><strong>${Number(
                    billTxnLogs.discountFlat
                  ).toFixed(2)}</strong></td>
                </tr>
              `
                  : ""
              }
              
              ${
                billTxnLogs.discountPercent > 0
                  ? `
                <tr>
                  <td class="text-right"><strong>Discount (${
                    billTxnLogs.discountPercent
                  }%):</strong></td>
                  <td class="text-right"><strong>${Number(
                    (billTxnLogs.discountPercent * billTxnLogs.subTotal) / 100
                  ).toFixed(2)}</strong></td>
                </tr>
              `
                  : ""
              }
              
              ${
                billTxnLogs.gst
                  ? `
                <tr>
                  <td class="text-right"><strong>CGST (${
                    billTxnLogs.cgstPercent
                  }%):</strong></td>
                  <td class="text-right"><strong>${Number(
                    billTxnLogs.cgst
                  ).toFixed(2)}</strong></td>
                </tr>
                <tr>
                  <td class="text-right"><strong>SGST (${
                    billTxnLogs.sgstPercent
                  }%):</strong></td>
                  <td class="text-right"><strong>${Number(
                    billTxnLogs.sgst
                  ).toFixed(2)}</strong></td>
                </tr>
              `
                  : ""
              }

              ${
                billTxnLogs.service && billTxnLogs.serviceAmount > 0
                  ? `
                <tr>
                  <td class="text-right"><strong>Service Charge (${
                    billTxnLogs.servicePercentage || 0
                  }%):</strong></td>
                  <td class="text-right"><strong>${Number(
                    billTxnLogs.serviceAmount
                  ).toFixed(2)}</strong></td>
                </tr>
              `
                  : ""
              }

              ${
                couponMetadata
                  ? `
                <tr>
                  <td class="text-right"><strong>Coupon Applied: </td>
                  <td class="text-right"><strong> - ${couponMetadata.value}</strong></td>
                </tr>
              `
                  : ""
              }
              
              ${
                billTxnLogs.tips > 0
                  ? `
                <tr>
                  <td class="text-right"><strong>Tips:</strong></td>
                  <td class="text-right"><strong>${Number(
                    billTxnLogs.tips
                  ).toFixed(2)}</strong></td>
                </tr>
              `
                  : ""
              }
              
              <tr class="total-row">
                <td class="text-right" style="padding-top: 8px;"><strong>GRAND TOTAL:</strong></td>
                <td class="text-right" style="padding-top: 8px;"><strong>${Number(
                  billTxnLogs.total
                ).toFixed(2)}</strong></td>
              </tr>
            </table>
          </div>

          <!-- Footer -->
          <div class="mt-12">
            <div class="divider"></div>
            ${
              billTxnLogs.gst &&
              !billTxnLogs.gstExcluded &&
              billTxnLogs.showGSTFooter
                ? `<p class="footer-text mb-6">GST Already Included in Item Price</p>`
                : ""
            }
            <p class="footer-text mb-4">Thank you for visiting!</p>
            <p class="footer-text">Powered by QRapid</p>
          </div>
        </div>

        <script>
          window.onload = function() {
            window.setTimeout(function() {
              window.print();
              window.setTimeout(function() {
                window.close();
              }, 500);
            }, 300);
          }
        </script>
      </body>
    </html>
  `;

  const opened = openThermalBillPrintWindow(billHtml);
  if (!opened.ok && opened.reason === "popup_blocked") {
    toast.error("Allow pop-ups for this site to open the print window.");
  }
}
